// Nadrama <https://nadrama.com>
// Copyright Nadrama Pty Ltd
// All rights reserved.
import { For, Show, createSignal, onCleanup, onMount } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useLocation } from "@tanstack/solid-router";
import BookOpen from "lucide-solid/icons/book-open";
import ChartPie from "lucide-solid/icons/chart-pie";
import ChevronDown from "lucide-solid/icons/chevron-down";
import CircleHelp from "lucide-solid/icons/circle-help";
import CircleUserRound from "lucide-solid/icons/circle-user-round";
import Cloud from "lucide-solid/icons/cloud";
import LayoutDashboard from "lucide-solid/icons/layout-dashboard";
import LogOut from "lucide-solid/icons/log-out";
import Menu from "lucide-solid/icons/menu";
import Monitor from "lucide-solid/icons/monitor";
import Moon from "lucide-solid/icons/moon";
import FolderKanban from "lucide-solid/icons/folder-kanban";
import UsersRound from "lucide-solid/icons/users-round";
import Server from "lucide-solid/icons/server";
import Settings from "lucide-solid/icons/settings";
import Sun from "lucide-solid/icons/sun";
import Users from "lucide-solid/icons/users";
import X from "lucide-solid/icons/x";
import type { LucideIcon } from "lucide-solid";
import { cliMe, listAccounts } from "./api/generated";
import { authSlotHeaders } from "./api/runtime";
import { AppLink, useGo } from "./navigation";
import { Notifications } from "./notifications";

const path = () => location.pathname;
type Theme = "light" | "dark";
const savedTheme = localStorage.getItem("nadrama-theme");
const [theme, setTheme] = createSignal<Theme>(
  savedTheme === "dark" ? "dark" : "light",
);
document.documentElement.dataset.theme = theme();
function ThemeToggle() {
  const dark = () => theme() === "dark";
  return (
    <button
      class="header-action inline-flex"
      aria-label={dark() ? "Switch to light mode" : "Switch to dark mode"}
      title={dark() ? "Switch to light mode" : "Switch to dark mode"}
      onClick={() => {
        const next = dark() ? "light" : "dark";
        setTheme(next);
        document.documentElement.dataset.theme = next;
        localStorage.setItem("nadrama-theme", next);
      }}
    >
      {dark() ? <Sun size={21} /> : <Moon size={21} />}
    </button>
  );
}
type NavItem = { label: string; route: string; icon: LucideIcon };
type NavGroup = { label?: string; items: NavItem[] };
const nav: NavGroup[] = [
  {
    items: [
      { label: "Overview", route: "", icon: LayoutDashboard },
      { label: "Projects", route: "projects", icon: FolderKanban },
    ],
  },
  {
    label: "Infrastructure",
    items: [
      { label: "Cloud Providers", route: "providers", icon: Cloud },
      { label: "Clusters", route: "clusters", icon: Server },
    ],
  },
  {
    label: "Account",
    items: [
      // Billing is intentionally hidden until it is ready for users.
      // {label:'Billing',route:'billing',icon:CreditCard},
      { label: "Groups", route: "groups", icon: UsersRound },
      { label: "Members", route: "members", icon: Users },
      { label: "Settings", route: "settings", icon: Settings },
    ],
  },
  {
    label: "User",
    items: [{ label: "Sessions", route: "sessions", icon: Monitor }],
  },
];
export function AccountShell(p: {
  slot: number;
  account: string;
  children: unknown;
}) {
  const go = useGo();
  const [drawer, setDrawer] = createSignal(false);
  const [accountOpen, setAccountOpen] = createSignal(false);
  let accountMenu!: HTMLDivElement;
  const shellLocation = useLocation();
  const accounts = createQuery(() => ({
    queryKey: [p.slot, "accounts"],
    queryFn: () =>
      listAccounts({
        throwOnError: true,
        responseStyle: "fields",
        headers: authSlotHeaders(p.slot),
      }),
  }));
  const identity = createQuery(() => ({
    queryKey: [p.slot, "identity"],
    queryFn: () =>
      cliMe({
        throwOnError: true,
        responseStyle: "fields",
        headers: authSlotHeaders(p.slot),
      }),
  }));
  const current = () =>
    accounts.data?.data.items?.find((item) => item.id === p.account);
  const base = `/u${p.slot}/${p.account}`;
  const active = (route: string) =>
    route
      ? shellLocation().pathname.startsWith(`${base}/${route}`)
      : shellLocation().pathname === base ||
        shellLocation().pathname === `${base}/`;
  onMount(() => {
    const closeAccountMenuOutside = (event: PointerEvent) => {
      if (accountOpen() && !accountMenu.contains(event.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("pointerdown", closeAccountMenuOutside);
    onCleanup(() =>
      document.removeEventListener("pointerdown", closeAccountMenuOutside),
    );
  });
  return (
    <div class="min-h-screen">
      <header class="brand fixed inset-x-0 top-0 z-50 flex h-16 items-center px-4 text-white">
        <button
          class="header-action mr-2 inline-flex md:hidden"
          aria-label="Open navigation"
          onClick={() => setDrawer(true)}
        >
          <Menu size={22} />
        </button>
        <img src="/images/nadrama-nav-logo.png" alt="Nadrama" class="h-8" />
        <div class="ml-auto flex items-center gap-1">
          <ThemeToggle />
          <Notifications slot={p.slot} account={p.account} />
          <div ref={accountMenu} class="relative">
            <button
              class="header-account"
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={accountOpen()}
              onClick={() => setAccountOpen(!accountOpen())}
            >
              <CircleUserRound size={28} />
              <ChevronDown size={16} />
            </button>
            <Show when={accountOpen()}>
              <div
                role="menu"
                class="popover absolute right-0 top-full z-[70] mt-2 w-64 text-slate-800"
              >
                <div class="account-menu-user">
                  <p class="text-sm uppercase tracking-wider text-slate-500">
                    Current user
                  </p>
                  <p class="truncate text-base font-semibold text-slate-900">
                    {identity.data?.data.name || "Name not set"}
                  </p>
                  <p class="truncate text-sm text-slate-500">
                    {identity.data?.data.email ?? "Signed-in user"}
                  </p>
                </div>
                <button
                  role="menuitem"
                  class="popover-item"
                  onClick={() => {
                    setAccountOpen(false);
                    go(`${base}/profile`);
                  }}
                >
                  <CircleUserRound size={17} />
                  Edit profile
                </button>
                <div class="account-menu-account">
                  <p class="text-sm uppercase tracking-wider text-slate-500">
                    Current account
                  </p>
                  <p class="truncate text-base font-semibold text-slate-900">
                    {current()?.name ?? "Account"}
                  </p>
                </div>
                <button
                  role="menuitem"
                  class="popover-item"
                  onClick={() => {
                    setAccountOpen(false);
                    go(`${base}/settings`);
                  }}
                >
                  <Settings size={17} />
                  Account settings
                </button>
                <button
                  role="menuitem"
                  class="popover-item"
                  onClick={() => {
                    setAccountOpen(false);
                    go(`/select?destination=${encodeURIComponent(path())}`);
                  }}
                >
                  <ChartPie size={17} />
                  Switch account
                </button>
                <div class="account-menu-signout">
                  <button
                    role="menuitem"
                    class="popover-item text-rose-700"
                    onClick={() => {
                      setAccountOpen(false);
                      go(`/logout?destination=${encodeURIComponent(path())}`);
                    }}
                  >
                    <LogOut size={17} />
                    Sign out
                  </button>
                </div>
              </div>
            </Show>
          </div>
        </div>
      </header>
      <Show when={drawer()}>
        <button
          class="fixed inset-0 z-[55] bg-slate-950/40 md:hidden"
          aria-label="Close navigation"
          onClick={() => setDrawer(false)}
        />
      </Show>
      <aside
        class={`sidebar fixed bottom-0 left-0 top-16 z-[60] w-64 transition-transform md:translate-x-0 ${drawer() ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div class="flex justify-end p-2 md:hidden">
          <button
            class="icon-action"
            aria-label="Close navigation"
            onClick={() => setDrawer(false)}
          >
            <X size={20} />
          </button>
        </div>
        <nav aria-label="Account navigation" class="px-3 pb-3 pt-0 md:p-3">
          <For each={nav}>
            {(group) => (
              <section class="sidebar-group">
                <Show when={group.label}>
                  <h2>{group.label}</h2>
                </Show>
                <ul>
                  <For each={group.items}>
                    {(item) => {
                      const Icon = item.icon;
                      return (
                        <li>
                          <AppLink
                            classList={{
                              "sidebar-link": true,
                              "sidebar-link-active": active(item.route),
                            }}
                            to={`${base}/${item.route}`}
                            onNavigate={() => setDrawer(false)}
                            aria-current={
                              active(item.route) ? "page" : undefined
                            }
                          >
                            <Icon size={19} />
                            <span>{item.label}</span>
                          </AppLink>
                        </li>
                      );
                    }}
                  </For>
                  <Show when={group.label === "User"}>
                    <li>
                      <AppLink
                        class="sidebar-link"
                        to={`/logout?destination=${encodeURIComponent(shellLocation().pathname)}`}
                      >
                        <LogOut size={19} />
                        <span>Sign Out</span>
                      </AppLink>
                    </li>
                  </Show>
                </ul>
              </section>
            )}
          </For>
          <section class="sidebar-group">
            <ul>
              <li>
                <a
                  data-document-navigation
                  class="sidebar-link"
                  href="https://nadrama.com/docs"
                >
                  <BookOpen size={19} />
                  <span>Documentation</span>
                </a>
              </li>
              <li>
                <a
                  data-document-navigation
                  class="sidebar-link"
                  href="https://nadrama.com/contact"
                >
                  <CircleHelp size={19} />
                  <span>Help/Feedback</span>
                </a>
              </li>
            </ul>
          </section>
        </nav>
      </aside>
      <main class="app-main ml-0 min-h-screen px-4 pb-16 pt-24 md:ml-64 md:px-10">
        <div class="mx-auto max-w-6xl">{p.children as never}</div>
      </main>
    </div>
  );
}
